import { useCallback, useEffect, useRef } from 'react';
import { useFetchAccountStatusMutation } from '@services/account/account';
import { AccountStatus } from '@models/account';

/** Gap between polls, matching the upgrade lane's foreground interval. */
const POLL_INTERVAL_MS = 2_000;

/**
 * How long to keep watching before giving up on reporting.
 *
 * A bound is required rather than optional, because nothing reports that a combination failed to
 * finish: a stuck workflow is indistinguishable from a slow one. Two minutes rather than the
 * dialog's ten seconds because a combination is slow by nature, so a short window would give up
 * almost every time.
 */
const POLL_DEADLINE_MS = 120_000;

/**
 * What became of a combination that was watched.
 *
 * `stoppedWatching` is deliberately not a failure. The work continues server-side after the watch
 * ends, and nothing observed it failing, so reporting either outcome would assert more than is
 * known.
 *
 * `unreadable` is separate from it because the two are not the same thing. A watch whose every read
 * failed learned nothing at all, and reporting that as still running would hand someone a
 * reassurance drawn from an endpoint that never answered.
 */
export type CombineOutcome =
  | 'combined'
  | 'degraded'
  | 'unreadable'
  | 'stoppedWatching';

/**
 * Follows a combination after the answer has been accepted, so what gets reported is what happened
 * rather than what was accepted.
 *
 * The service answers as soon as it has taken the answer, and the work that actually combines the
 * accounts runs afterwards for tens of seconds. Treating acceptance as completion is what would tell
 * someone their accounts were combined while the combining was still going on.
 *
 * **The account's status is not the finish line.** A combination targets an account that is already
 * `CREATED` and stays `CREATED` for the whole operation, so the status never marks the work done.
 * What changes is a completion marker the workflow's final step writes, which the account-status
 * read reports as `isRegistrationWorkflowComplete`.
 *
 * **The status is still worth reading, for the opposite reason.** The workflow recomputes the
 * account's status near the end without regard for what it was before, so a combination can leave a
 * previously healthy account degraded. That is a real failure, and the status is the only thing that
 * reports it.
 */
export const useCombineCompletion = () => {
  const [fetchAccountStatus] = useFetchAccountStatusMutation();
  const abandoned = useRef(false);

  /*
    Stops the watch when the caller goes away. Without this the loop would keep polling after
    unmount and resolve into state that no longer exists.

    Cleared on mount as well as set on unmount, which is not redundant: React mounts, unmounts and
    remounts effects on the first render in development, so a flag only ever set by the cleanup is
    already true by the time anything calls this, and every watch exits before its first read.
  */
  useEffect(() => {
    abandoned.current = false;
    return () => {
      abandoned.current = true;
    };
  }, []);

  return useCallback(
    async (
      globalAccountId: string | undefined,
      /*
        The window is injectable so a test can reach the branches that only happen at its
        end without spending two real minutes doing so. Callers in the app pass nothing.
      */
      options?: { deadlineMs?: number },
    ): Promise<CombineOutcome> => {
      /*
        Nothing to watch, which is not the same as watched and finished. Reporting completion here
        would raise a success report for work never observed, so this reports that the watch could
        not be made instead. It should not happen: an invitation the service accepted for combining
        names the account it combines into. If it ever does, a caller is better told nothing is
        known than told it succeeded.
      */
      if (!globalAccountId) {
        return 'unreadable';
      }

      const deadline = Date.now() + (options?.deadlineMs ?? POLL_DEADLINE_MS);
      let firstPoll = true;
      let everRead = false;

      while (!abandoned.current && Date.now() < deadline) {
        /*
          Asked before the first wait, so a combination that has already finished is reported at once
          rather than after an interval the user would sit through for nothing.
        */
        if (firstPoll) {
          firstPoll = false;
        } else {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          if (abandoned.current) {
            break;
          }
        }

        let status;
        try {
          status = await fetchAccountStatus({ globalAccountId }).unwrap();
          everRead = true;
          /*
            Checked again here, not only after the sleep: a read in flight when the caller
            goes away would otherwise resolve an outcome for a caller that no longer exists,
            and send it on to refresh a list nobody is looking at.
          */
          if (abandoned.current) {
            break;
          }
        } catch {
          /*
            A failed read says nothing about the combination, which continues regardless. The loop
            keeps asking until the deadline rather than reporting an outcome it has not observed.
          */
          continue;
        }

        /*
          The status is checked before the completion marker, deliberately. The workflow writes the
          status immediately before writing the marker, so the two arriving together means the
          combination finished and left the account worse than it found it. Reading the marker first
          would report that as success.
        */
        if (
          status.accountStatus === AccountStatus.Disabled ||
          status.accountStatus === AccountStatus.PartiallyCreated
        ) {
          return 'degraded';
        }

        if (status.isRegistrationWorkflowComplete) {
          return 'combined';
        }
      }

      /*
        Told apart on purpose. A watch that read the account and never saw it finish knows the work
        is still going; a watch whose every read failed knows nothing, and saying "still running"
        there would be a reassurance with nothing behind it.
      */
      return everRead ? 'stoppedWatching' : 'unreadable';
    },
    [fetchAccountStatus],
  );
};

export default useCombineCompletion;
